import { Column, Table } from '@tanstack/react-table';
import React, { useState } from 'react';
import { LuFilter } from 'react-icons/lu';

import { FilterType } from '@/cip/_openapi/portfolio-optimizer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface ColumnFilterProps<T> {
    column: Column<T, unknown>;
    table: Table<T>;
}

export function ColumnFilter<T>({ column, table }: ColumnFilterProps<T>) {
    const [open, setOpen] = useState(false);
    const filterValue = column.getFilterValue();
    const filterType = column.columnDef.meta?.filterType || FilterType.STRING;
    const isFiltered = column.getIsFiltered();
    const close = () => setOpen(false);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="ghost"
                    size="sm"
                    aria-label={isFiltered ? `Edit filter for ${column.id}` : `Filter ${column.id}`}
                    className={`h-6 w-6 shrink-0 p-0 transition-opacity ${isFiltered ? 'text-blue-600' : 'text-gray-400'} ${
                        isFiltered || open
                            ? 'opacity-100'
                            : 'opacity-0 group-focus-within/header:opacity-100 group-hover/header:opacity-100'
                    }`}
                >
                    <LuFilter aria-hidden="true" style={{ width: 14, height: 14 }} />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="start">
                <FilterContent
                    column={column}
                    table={table}
                    filterType={filterType}
                    filterValue={filterValue}
                    onClose={close}
                />
            </PopoverContent>
        </Popover>
    );
}

function FilterContent<T>({
    column,
    table,
    filterType,
    filterValue,
    onClose,
}: {
    column: Column<T, unknown>;
    table: Table<T>;
    filterType: string;
    filterValue: any;
    onClose: () => void;
}) {
    switch (filterType) {
        case FilterType.STRING:
            return <TextFilter column={column} filterValue={filterValue as string[] | undefined} onClose={onClose} />;
        case FilterType.LIST:
            return (
                <MultiSelectFilter
                    column={column}
                    table={table}
                    filterValue={filterValue as string[] | undefined}
                    onClose={onClose}
                />
            );
        case FilterType.NUMBER:
            return <NumberFilter column={column} filterValue={filterValue} onClose={onClose} />;
        case FilterType.DATE:
            return <DateFilter column={column} filterValue={filterValue} onClose={onClose} />;
        case FilterType.BOOLEAN:
            return <BooleanFilter column={column} filterValue={filterValue as boolean | undefined} onClose={onClose} />;
        default:
            return <TextFilter column={column} filterValue={filterValue as string[] | undefined} onClose={onClose} />;
    }
}

function TextFilter<T>({
    column,
    filterValue,
    onClose,
}: {
    column: Column<T, unknown>;
    filterValue?: string[];
    onClose: () => void;
}) {
    const initialValue = Array.isArray(filterValue) && filterValue.length > 0 ? filterValue[0] : '';
    const [value, setValue] = useState(initialValue);

    return (
        <div className="space-y-2">
            <div className="text-sm font-medium">Filter by text</div>
            <Input
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Enter text..."
                className="h-8"
            />
            <div className="flex gap-2">
                <Button
                    size="sm"
                    onClick={() => {
                        column.setFilterValue(value ? [value] : undefined);
                        onClose();
                    }}
                    className="flex-1"
                >
                    Apply
                </Button>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                        setValue('');
                        column.setFilterValue(undefined);
                        onClose();
                    }}
                    className="flex-1"
                >
                    Clear
                </Button>
            </div>
        </div>
    );
}

function MultiSelectFilter<T>({
    column,
    table,
    filterValue,
    onClose,
}: {
    column: Column<T, unknown>;
    table: Table<T>;
    filterValue?: string[];
    onClose: () => void;
}) {
    const options = (column.columnDef.meta?.filterOptionsFn?.(table) as string[]) || [];
    const formatOption = column.columnDef.meta?.formatOption ?? ((v: string) => v);
    const [selected, setSelected] = useState<string[]>(filterValue || []);

    const toggleOption = (option: string) => {
        setSelected((prev) => (prev.includes(option) ? prev.filter((v) => v !== option) : [...prev, option]));
    };

    return (
        <div className="space-y-2">
            <div className="text-sm font-medium">Select options</div>
            <div className="max-h-64 space-y-1 overflow-y-auto">
                {options.map((option) => (
                    <label
                        key={option}
                        className="flex cursor-pointer items-center gap-2 rounded p-1 hover:bg-gray-100"
                    >
                        <input
                            type="checkbox"
                            checked={selected.includes(option)}
                            onChange={() => toggleOption(option)}
                            className="h-4 w-4"
                        />
                        <span className="text-sm">{formatOption(option)}</span>
                    </label>
                ))}
            </div>
            <div className="flex gap-2">
                <Button
                    size="sm"
                    onClick={() => {
                        column.setFilterValue(selected.length ? selected : undefined);
                        onClose();
                    }}
                    className="flex-1"
                >
                    Apply ({selected.length})
                </Button>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                        setSelected([]);
                        column.setFilterValue(undefined);
                        onClose();
                    }}
                    className="flex-1"
                >
                    Clear
                </Button>
            </div>
        </div>
    );
}

function NumberFilter<T>({
    column,
    filterValue,
    onClose,
}: {
    column: Column<T, unknown>;
    filterValue: any;
    onClose: () => void;
}) {
    const parsed = typeof filterValue === 'object' ? filterValue : {};
    const [eq, setEq] = useState(parsed.eq || '');
    const [gte, setGte] = useState(parsed.gte || '');
    const [lte, setLte] = useState(parsed.lte || '');

    const applyFilter = () => {
        const filter: any = {};
        if (eq) {
            filter.eq = eq; // Keep as string, processFilter will convert to number
        }
        if (gte) {
            filter.gte = gte;
        }
        if (lte) {
            filter.lte = lte;
        }

        // Add mode if we have range filters
        if ((gte || lte) && !eq) {
            filter.mode = 'AND';
        }

        if (Object.keys(filter).length > 0) {
            column.setFilterValue(filter);
        } else {
            column.setFilterValue(undefined);
        }
        onClose();
    };

    return (
        <div className="space-y-2">
            <div className="text-sm font-medium">Filter by number</div>
            <div className="space-y-2">
                <div>
                    <label className="text-xs text-gray-600">Equals</label>
                    <Input
                        type="number"
                        value={eq}
                        onChange={(e) => setEq(e.target.value)}
                        placeholder="Value..."
                        className="h-8"
                        disabled={!!(gte || lte)}
                    />
                </div>
                <div className="text-xs text-gray-500">OR</div>
                <div>
                    <label className="text-xs text-gray-600">Greater than or equal (≥)</label>
                    <Input
                        type="number"
                        value={gte}
                        onChange={(e) => setGte(e.target.value)}
                        placeholder="Min..."
                        className="h-8"
                        disabled={!!eq}
                    />
                </div>
                <div>
                    <label className="text-xs text-gray-600">Less than or equal (≤)</label>
                    <Input
                        type="number"
                        value={lte}
                        onChange={(e) => setLte(e.target.value)}
                        placeholder="Max..."
                        className="h-8"
                        disabled={!!eq}
                    />
                </div>
            </div>
            <div className="flex gap-2">
                <Button size="sm" onClick={applyFilter} className="flex-1">
                    Apply
                </Button>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                        setEq('');
                        setGte('');
                        setLte('');
                        column.setFilterValue(undefined);
                        onClose();
                    }}
                    className="flex-1"
                >
                    Clear
                </Button>
            </div>
        </div>
    );
}

function DateFilter<T>({
    column,
    filterValue,
    onClose,
}: {
    column: Column<T, unknown>;
    filterValue: any;
    onClose: () => void;
}) {
    const parsed = typeof filterValue === 'object' ? filterValue : {};
    const [from, setFrom] = useState(parsed.from || '');
    const [to, setTo] = useState(parsed.to || '');

    const applyFilter = () => {
        if (from || to) {
            column.setFilterValue({ ...(from && { from }), ...(to && { to }) });
        } else {
            column.setFilterValue(undefined);
        }
        onClose();
    };

    return (
        <div className="space-y-2">
            <div className="text-sm font-medium">Filter by date</div>
            <div className="space-y-2">
                <div>
                    <label className="text-xs text-gray-600">From</label>
                    <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8" />
                </div>
                <div>
                    <label className="text-xs text-gray-600">To</label>
                    <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8" />
                </div>
            </div>
            <div className="flex gap-2">
                <Button size="sm" onClick={applyFilter} className="flex-1">
                    Apply
                </Button>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                        setFrom('');
                        setTo('');
                        column.setFilterValue(undefined);
                        onClose();
                    }}
                    className="flex-1"
                >
                    Clear
                </Button>
            </div>
        </div>
    );
}

function BooleanFilter<T>({
    column,
    filterValue,
    onClose,
}: {
    column: Column<T, unknown>;
    filterValue?: boolean;
    onClose: () => void;
}) {
    return (
        <div className="space-y-2">
            <div className="text-sm font-medium">Filter by value</div>
            <Select
                value={filterValue === undefined ? 'all' : filterValue ? 'true' : 'false'}
                onValueChange={(v) => {
                    column.setFilterValue(v === 'all' ? undefined : v === 'true');
                    onClose();
                }}
            >
                <SelectTrigger className="h-8">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="true">True</SelectItem>
                    <SelectItem value="false">False</SelectItem>
                </SelectContent>
            </Select>
        </div>
    );
}
